// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { UsMiAssessmentSectionsSpec } from "../UsMiInitialClassification2026Policy/InitialQuestionSchema";

export const miBaseReviewClassificationQuestions = [
  {
    title: "Disciplinary history",
    questions: [
      {
        title: "Administrative segregation placements in past 36 months",
        type: "NUMBER",
        countDescriptor: "Placements",
        description:
          "Counts distinct administrative segregation placement starts (current or previous incarceration periods) in past 36 months.",
        bounds: 0,
        multiplier: 4.2,
        note: {
          header: "Administrative segregation placements, past 36 months",
          notfound: "None recorded in the past 36 months",
        },
      },
      {
        title:
          "Number of misconducts for assault, homicide, sexual violence, inciting a riot or strike in past 6 months",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description:
          "Includes any incarceration periods overlapping the past 6 months.",
        bounds: 0,
        multiplier: 12.3,
        note: {
          header: "Assaultive misconducts, past 6 months",
          notfound: "None recorded in the last 6 months",
        },
      },
      {
        title:
          "Number of misconducts for assault, homicide, sexual violence, inciting a riot or strike in past 6-12 months",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description:
          "Includes any incarceration periods overlapping the past 6-12 months.",
        bounds: 0,
        multiplier: 7.5,
        note: {
          header: "Assaultive misconducts, past 6-12 months",
          notfound: "None recorded in the last 6-12 months",
        },
      },
      {
        title:
          "Number of misconducts for assault, homicide, sexual violence, inciting a riot or strike in past 12-24 months",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description:
          "Includes any incarceration periods overlapping the past 12-24 months.",
        bounds: 0,
        multiplier: 4.6,
        note: {
          header: "Assaultive misconducts, past 12-24 months",
          notfound: "None recorded in the last 12-24 months",
        },
      },
      {
        title:
          "Number of misconducts for assault, homicide, sexual violence, inciting a riot or strike in past 24-36 months",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description:
          "Includes any incarceration periods overlapping the past 24-36 months.",
        bounds: 0,
        multiplier: 3.7,
        note: {
          header: "Assaultive misconducts, past 24-36 months",
          notfound: "None recorded in the last 24-36 months",
        },
      },
      {
        title:
          "Number of misconducts for assault, homicide, sexual violence, inciting a riot or strike in past incarceration period(s), within past 36 months (max 4)",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        bounds: 4,
        description:
          "Includes any incarceration periods overlapping the past 36 months.",
        multiplier: 3.7,
        note: {
          header: "Assaultive misconducts, past 36 months",
          notfound: "None recorded in the last 36 months",
        },
      },
      {
        title:
          "Number of misconducts for fighting, threatening behavior, or possession of a weapon in past 6 months",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description:
          "Includes any incarceration periods overlapping the past 6 months.",
        bounds: 0,
        multiplier: 6,
        note: {
          header: "Fighting misconducts, past 6 months",
          notfound: "None recorded in the last 6 months",
        },
      },
      {
        title:
          "Number of misconducts for fighting, threatening behavior, or possession of a weapon in past 6-12 months",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description:
          "Includes any incarceration periods overlapping the past 6-12 months.",
        bounds: 0,
        multiplier: 3.5,
        note: {
          header: "Fighting misconducts, past 6-12 months",
          notfound: "None recorded in the last 6-12 months",
        },
      },
      {
        title:
          "Number of misconducts for fighting, threatening behavior, or possession of a weapon in past 12-36 months",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description:
          "Includes any incarceration periods overlapping the past 12-36 months.",
        bounds: 0,
        multiplier: 2,
        note: {
          header: "Fighting misconducts, past 12-36 months",
          notfound: "None recorded in the last 12-36 months",
        },
      },
      {
        title:
          "Number of misconducts for fighting, threatening behavior, or possession of a weapon in past incarceration period(s), within past 36 months (max 4)",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description:
          "Includes any incarceration periods overlapping the past 36 months.",
        bounds: 4,
        multiplier: 2,
        note: {
          header: "Fighting misconducts, past 36 months",
          notfound: "None recorded in the last 36 months",
        },
      },
      {
        title:
          "Number of other Class I or Class II misconducts in past 6 months in current incarceration period",
        type: "NUMBER",
        countDescriptor: "Misconducts",
        description: "Auto-filled from misconduct records.",
        bounds: 0,
        multiplier: 1,
        note: {
          header: "Other non-violent Class I and II misconducts, past 6 months",
          notfound: "None recorded in the past 6 months.",
        },
      },
    ],
  },
  {
    title: "Good behavior and programming",
    questions: [
      {
        title: "Program completion since 2021 - Core (max 2)",
        type: "NUMBER",
        countDescriptor: "Programs",
        description:
          "Count of Core program completions in the current incarceration period.",
        bounds: 2,
        multiplier: -2.1,
        note: {
          header: "Count of Core program completions",
          notfound: "No Core program completions recorded",
        },
      },
      {
        title: "Program completion since 2021 - High",
        type: "SINGLE",
        description:
          "Has High program completions in the current incarceration period.",
        options: [
          { text: "True", score: -2.3 },
          { text: "False", score: 0 },
        ],
        note: {
          header: "Count of High program completions",
          notfound: "No High program completions recorded",
        },
      },
      {
        title: "Work participation",
        type: "NUMBER",
        countDescriptor: "Periods with satisfactory work",
        autofilled: false,
        description:
          "Not autofilled. Number of 4-month periods with satisfactory work performance in past 12 months. Satisfactory performance is determined by facility staff.",
        bounds: 3,
        multiplier: -1,
      },
    ],
  },
] satisfies UsMiAssessmentSectionsSpec[];

const miMaleAgeClassificationQuestions = {
  title: "Background factors",
  questions: [
    {
      title: "Age",
      type: "SINGLE",
      description: "Auto-filled from date of birth.",
      options: [
        { text: "25 or Younger", score: 13.1 },
        { text: "26-30", score: 11.2 },
        { text: "31-35", score: 10.5 },
        { text: "36-40", score: 9.1 },
        { text: "41-49", score: 8.8 },
        { text: "50 or Older", score: 8.0 },
      ],
      note: {
        header: "Age",
        notfound: "No age found",
      },
    },
    {
      title: "VFO score",
      type: "SINGLE",
      description:
        "Based on most recent COMPAS assessment on record (all time). “None” applies if the person has no COMPAS assessment on record.",
      options: [
        { text: "Low", score: -1.2 },
        { text: "Medium", score: 0 },
        { text: "High", score: 2.1 },
        { text: "None", score: 0 },
      ],
      note: {
        header: "Most recent COMPAS assessment",
        notfound: "No VFO score found",
      },
    },
  ],
} satisfies UsMiAssessmentSectionsSpec;

export const miMaleReviewClassificationQuestions = [
  miMaleAgeClassificationQuestions,
  ...miBaseReviewClassificationQuestions,
] satisfies UsMiAssessmentSectionsSpec[];

const miFemaleAgeClassificationQuestions = {
  title: "Background factors",
  questions: [
    {
      title: "Age",
      type: "SINGLE",
      description: "Auto-filled from date of birth.",
      options: [
        { text: "25 or Younger", score: 13.1 },
        { text: "26-30", score: 11.2 },
        { text: "31-35", score: 10.5 },
        { text: "36-40", score: 9.1 },
        { text: "41-49", score: 8.8 },
        { text: "50 or Older", score: 8.0 },
      ],
      note: {
        header: "Age",
        notfound: "No age found",
      },
    },
    {
      title: "VFO score",
      type: "SINGLE",
      description:
        "Based on most recent COMPAS assessment on record (all time). “None” applies if the person has no COMPAS assessment on record.",
      options: [
        { text: "Low", score: -1.2 },
        { text: "Medium", score: 0 },
        { text: "None", score: 0 },
      ],
      note: {
        header: "Most recent COMPAS assessment",
        notfound: "No VFO score found",
      },
    },
  ],
} satisfies UsMiAssessmentSectionsSpec;

export const miFemaleReviewClassificationQuestions = [
  miFemaleAgeClassificationQuestions,
  ...miBaseReviewClassificationQuestions,
] satisfies UsMiAssessmentSectionsSpec[];
